import * as React from 'react';
import { Box, Button, Chip, Grid, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { ReactComponent as IconAdditionalServiceSvg } from 'assets/images/icon_additional_service.svg';
import { ReactComponent as IconCompletedScheduleSvg } from 'assets/images/icon_completed_schedule.svg';
import { ReactComponent as IconMissedScheduleSvg } from 'assets/images/icon_missed_schedule.svg';
import { ReactComponent as IconClockSvg } from 'assets/images/icon_clock.svg';
import { IZoneCleaningHistoryItemView } from 'models';
import { EZoneCleaningHistoryItemStatus } from 'enums';

dayjs.extend(customParseFormat);

interface ZoneCleaningHistoryItemCardProps {
  date: string;
  item: IZoneCleaningHistoryItemView;
}

export const ZoneCleaningHistoryItemCard: React.FC<ZoneCleaningHistoryItemCardProps> = ({ date, item }) => {
  const [showAll, setShowAll] = React.useState<boolean>(false);
  const theme = useTheme();

  const renderStatusIcon = ({
    status,
    width = 16,
    height = 16,
  }: {
    status?: string;
    width?: number;
    height?: number;
  }) =>
    status === EZoneCleaningHistoryItemStatus.ADDITIONAL_SERVICE ? (
      <IconAdditionalServiceSvg width={width} height={height} />
    ) : status === EZoneCleaningHistoryItemStatus.COMPLETED ? (
      <IconCompletedScheduleSvg width={width} height={height} />
    ) : status === EZoneCleaningHistoryItemStatus.MISSED ? (
      <IconMissedScheduleSvg width={width} height={height} />
    ) : null;

  const statusLabel = {
    [EZoneCleaningHistoryItemStatus.COMPLETED]: 'COMPLETED SCHEDULE',
    [EZoneCleaningHistoryItemStatus.ADDITIONAL_SERVICE]: 'SERVICE',
    [EZoneCleaningHistoryItemStatus.MISSED]: 'MISSED SCHEDULE',
    [EZoneCleaningHistoryItemStatus.REMAINS]: '',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: 66,
          height: 66,
          borderRadius: 33,
          border: `8px solid #ffffff`,
          bgcolor: item.log_type !== EZoneCleaningHistoryItemStatus.REMAINS ? theme.palette.primary.main : '#D9D9D9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          paddingBottom: item.log_type === EZoneCleaningHistoryItemStatus.MISSED ? 0.5 : 0,
        }}
      >
        {renderStatusIcon({ status: item.log_type, width: 30, height: 30 })}
      </Box>

      <Box
        sx={{
          width: 36,
          height: 2,
          bgcolor: '#C4C4C4',
        }}
      />

      <Box
        sx={{
          width: 370,
          minHeight: 140,
          border: `1px solid ${theme.palette.text.secondary}`,
          borderRadius: 1,
          paddingX: 1.2,
          paddingY: 1.5,
          bgcolor: item.log_type === EZoneCleaningHistoryItemStatus.MISSED ? '#FFECD6' : 'white',
          opacity: item.log_type ? 1 : 0.4,
          marginLeft: 1,
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: 16,
                color: theme.palette.primary.main,
              }}
            >
              {renderStatusIcon({ status: item.log_type })}
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 600,
                  marginLeft: 1,
                }}
                color="primary"
              >
                {statusLabel[item.log_type as EZoneCleaningHistoryItemStatus]}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                marginTop: 1,
                marginLeft: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 34,
                  fontWeight: 400,
                }}
              >
                {item.log_type === EZoneCleaningHistoryItemStatus.REMAINS || !item.updated_at
                  ? '--:--'
                  : dayjs(item.updated_at).format('hh:mm')}
              </Typography>

              {!(item.log_type === EZoneCleaningHistoryItemStatus.REMAINS || !item.updated_at) && (
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    marginBottom: 1.4,
                    marginLeft: 0.5,
                  }}
                >
                  {dayjs(item.updated_at).format('A')}
                </Typography>
              )}
            </Box>

            {item.scheduleTime && (
              <>
                <Typography
                  color="textSecondary"
                  sx={{
                    fontSize: 7,
                    fontWeight: 600,
                    marginTop: 1,
                  }}
                >
                  SCHEDULED TIME
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <IconClockSvg />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      marginLeft: 1,
                    }}
                  >
                    <Typography
                      color="textSecondary"
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      {dayjs(item.scheduleTime, 'HH:mm:ss').format('hh:mm')}
                    </Typography>
                    <Typography
                      color="textSecondary"
                      sx={{
                        fontSize: 8,
                        fontWeight: 600,
                        marginBottom: 0.4,
                      }}
                    >
                      {dayjs(item.scheduleTime, 'HH:mm:ss').format('A')}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Grid>

          <Grid item xs={7}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 400,
              }}
            >
              {dayjs(date).format('dddd')}
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 400,
              }}
            >
              {dayjs(date).format('MMMM D, YYYY')}
            </Typography>

            {item.service_item_labels.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  marginLeft: -0.4,
                  marginRight: -0.4,
                  marginTop: 1,
                }}
              >
                {item.service_item_labels
                  .slice(0, showAll ? item.service_item_labels.length : 5)
                  .map((serviceItemLabel, index) => (
                    <Box
                      key={`service-item-${index}`}
                      sx={{
                        padding: 0.4,
                      }}
                    >
                      <Chip
                        label={serviceItemLabel}
                        size="small"
                        sx={{
                          borderRadius: 1,
                          backgroundColor: `${theme.palette.primary.main}80`,
                          fontSize: 10,
                        }}
                      />
                    </Box>
                  ))}
              </Box>
            )}

            {!showAll && item.service_item_labels.length > 5 && (
              <Button
                sx={{
                  fontSize: 7,
                  paddingY: 0.3,
                  paddingX: 1,
                  minWidth: 'unset',
                  marginTop: 0.3,
                }}
                onClick={() => setShowAll(true)}
              >
                +{item.service_item_labels.length - 5} More
              </Button>
            )}
          </Grid>
        </Grid>
        {item.files?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography>Images</Typography>
            <Grid container spacing={1}>
              {item.files?.map((file) => (
                <Grid item xs={6} md={4} key={file.id}>
                  <img src={file.url} alt={file.name} style={{ width: 100 }} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
};
